/**
 * useProfessionalSearch
 * Hook for searching professionals (SP + FA) to assign as authorPro on resources.
 * Calls the lean GET /admin/resources/author-pro/search endpoint.
 * Returns User IDs (not ServiceProvider IDs) matching the new FK.
 *
 * Also exports useProfessionalById for resolving a selected user's display name
 * independently of the search results (fixes G13-3 bug 3).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';

type ProfessionalOption = {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
};

type ProfessionalDisplay = {
  id: string;
  name: string;
};

async function searchProfessionals(q?: string): Promise<ProfessionalOption[]> {
  const params = q ? { q } : {};
  const { data } = await apiClient.get<ProfessionalOption[]>(
    '/admin/resources/author-pro/search',
    { params },
  );
  return data;
}

function formatName(p: ProfessionalOption): string {
  const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ');
  if (p.companyName && fullName) return `${fullName} (${p.companyName})`;
  return p.companyName || fullName;
}

export function useServiceProviderSearch(
  search?: string,
): UseQueryResult<ProfessionalDisplay[], Error> {
  return useQuery({
    queryKey: ['admin', 'professionals', 'search', search] as const,
    queryFn: async () => {
      const results = await searchProfessionals(search);
      return results.map((p) => ({ id: p.id, name: formatName(p) }));
    },
    enabled: !!search && search.length >= 2,
    staleTime: 60_000,
  });
}

export function useProfessionalById(
  userId: string | undefined,
): UseQueryResult<ProfessionalDisplay | null, Error> {
  return useQuery({
    queryKey: ['admin', 'professionals', 'resolve', userId] as const,
    queryFn: async () => {
      if (!userId) return null;
      const results = await searchProfessionals();
      const match = results.find((p) => p.id === userId);
      return match ? { id: match.id, name: formatName(match) } : null;
    },
    enabled: !!userId,
    staleTime: 300_000,
  });
}
